'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import {
  useCreateTomVendingMachine,
  useDeleteTomVendingMachine,
  useTomVendingMachinesList,
  type TomVendingMachine,
} from '@/hooks/useTomVendingMachine';

type LocationFilter = 'all' | number;

const TomsVendingMachinesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: locations = [], isLoading: locationsLoading } = useTomVendingLocationsList();
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState<TomVendingMachine | null>(null);

  const locationIdParam =
    locationFilter === 'all' ? ('' as const) : (locationFilter as number);
  const { data: list = [], isLoading, refetch } = useTomVendingMachinesList(locationIdParam);
  const createMutation = useCreateTomVendingMachine();
  const deleteMutation = useDeleteTomVendingMachine();
  const { showSuccess, showError } = useNotification();

  const locationOptions = useMemo(
    () =>
      (locations as { id: number; name?: string }[]).map((loc) => ({
        label: loc.name || `Location ${loc.id}`,
        value: loc.id,
      })),
    [locations],
  );

  const filteredRows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return list;
    return list.filter((row) => {
      const name = (row.location?.name || '').toLowerCase();
      return (
        (row.title ?? '').toLowerCase().includes(t) ||
        (row.assetId || '').toLowerCase().includes(t) ||
        name.includes(t) ||
        String(row.id).includes(t)
      );
    });
  }, [list, search]);

  const defaultLocationId = locationOptions[0]?.value;

  const formFields = useMemo(
    () => [
      {
        groupName: 'Vending machine',
        fields: [
          {
            label: 'Location',
            name: 'locationId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: locationOptions,
          },
          {
            label: 'Order',
            name: 'sortOrder',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            required: true,
            columns: 6,
          },
          {
            label: 'Asset ID',
            name: 'assetId',
            type: GENERIC_FORM_FIELD_TYPE.TEXT,
            required: true,
            columns: 6,
          },
          { label: 'Title', name: 'title', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        ],
      },
    ],
    [locationOptions],
  );

  const parseLocationId = (v: unknown): number | null => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseInt(String(v), 10);
    return Number.isNaN(n) ? null : n;
  };

  const handleCreateSubmit = (values: Record<string, unknown>) => {
    const locationId = parseLocationId(values.locationId);
    if (locationId == null) {
      showError('Select a location');
      return;
    }
    const sortOrder =
      values.sortOrder === '' || values.sortOrder === undefined
        ? 0
        : parseInt(String(values.sortOrder), 10);
    if (Number.isNaN(sortOrder)) {
      showError('Order must be a number');
      return;
    }
    createMutation.mutate(
      {
        title: values.title,
        sortOrder,
        assetId: values.assetId,
        locationId,
      },
      {
        onSuccess: () => {
          showSuccess('Vending machine created');
          setCreateOpen(false);
          refetch();
        },
        onError: (e: any) => showError(e?.message || 'Failed to create'),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteMutation.mutate(
      { id: deleting.id },
      {
        onSuccess: () => {
          showSuccess('Vending machine deleted');
          setDeleteOpen(false);
          setDeleting(null);
          refetch();
        },
        onError: (e: any) => showError(e?.message || 'Failed to delete'),
      },
    );
  };

  const columns = useMemo<MRT_ColumnDef<TomVendingMachine>[]>(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => {
                const locId = row.original.location?.id;
                if (locId == null) {
                  showError('This machine has no location; open it from a location page to manage.');
                  return;
                }
                navigate(`/app/toms-vending/locations/${locId}/machines/${row.original.id}`);
              }}
              aria-label="Manage machine and inventory"
            >
              <ManageSearchIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setDeleting(row.original);
                setDeleteOpen(true);
              }}
              aria-label="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      { accessorKey: 'id', header: 'ID', size: 70 },
      { accessorKey: 'sortOrder', header: 'Order', size: 90 },
      { accessorKey: 'assetId', header: 'Asset ID' },
      {
        id: 'locationName',
        header: 'Location',
        Cell: ({ row }) => {
          const name = row.original.location?.name || '—';
          const wh = tomVendingLocationIsWarehouse(row.original.location as { isWarehouse?: boolean } | undefined);
          if (!wh) return name;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <span>{name}</span>
              <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
            </Box>
          );
        },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null | undefined;
          return v ? v : '—';
        },
      },
    ],
    [navigate, showError],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Vending machines' },
  ];

  const rightContent = (
    <Button
      onClick={() => setCreateOpen(true)}
      variant="contained"
      disabled={!locationOptions.length}
      sx={{ textTransform: 'none' }}
    >
      + Add vending machine
    </Button>
  );

  return (
    <Box>
      <GenericFormModal
        visible={createOpen}
        onDismiss={() => setCreateOpen(false)}
        onSubmit={handleCreateSubmit}
        initialValues={{
          locationId: defaultLocationId ?? '',
          title: '',
          sortOrder: 0,
          assetId: '',
        }}
        fieldGroups={formFields}
        title="Add vending machine"
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete vending machine</DialogTitle>
        <DialogContent>
          <Typography>
            Delete vending machine <strong>{deleting?.assetId}</strong>
            {deleting?.title?.trim() ? ` (${deleting.title.trim()})` : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <TitleBar breadcrumbs={breadcrumbs} title="Vending machines" rightContent={rightContent} />

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            select
            label="Filter by location"
            size="small"
            value={locationFilter === 'all' ? 'all' : String(locationFilter)}
            onChange={(e) => {
              const v = e.target.value;
              setLocationFilter(v === 'all' ? 'all' : parseInt(v, 10));
            }}
            sx={{ minWidth: 240 }}
            disabled={locationsLoading}
          >
            <MenuItem value="all">All locations</MenuItem>
            {(locations as { id: number; name?: string; isWarehouse?: boolean }[]).map((loc) => (
              <MenuItem key={loc.id} value={String(loc.id)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{loc.name || `Location ${loc.id}`}</span>
                  {tomVendingLocationIsWarehouse(loc) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" sx={{ ml: 'auto' }} />
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            placeholder="Search title, asset ID, location…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 320 }}
          />
        </Box>

        {!locationOptions.length && !locationsLoading && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Add a Toms Vending location first, then you can create vending machines.
          </Typography>
        )}

        <MaterialReactTable
          columns={columns}
          data={filteredRows}
          state={{ isLoading: isLoading || locationsLoading }}
          initialState={{ density: 'compact' }}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingMachinesPage;
